import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MAX_BID_DEFAULT_SUGGESTION_INT, OptimizationTypes, params } from '@app/configurations';
import { microsToNumber } from '@app/core/utilities/number.utilities';
import { SelectCampaignsListItem } from '@app/pg-goals/pg-goals.models';
import { SetMaxBid } from '@app/pg-goals/state/max-bid/max-bid.actions';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { addDays, differenceInDays } from 'date-fns';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share, switchMap, take } from 'rxjs/operators';
import { HistoricChartConfig, HistoryData, PGListItem } from '../../pg-goals/pg-list/pg-list.models';
import {
  adjustMonetaryValues,
  ExlcudeFewWarningsForEmptyPG,
  FormattedWarnings,
  GetCampaignsWithAllMetrics,
  GetDatesRequestInfo,
  getGoalsCurrentData,
  getGoalsWithUserCurrency,
  getMaxBidWithUserCurrency,
  GoalsSummaryData,
  mapMetricsForOpAndGoal,
  MaxBidValueForUI,
} from '../../pg-goals/shared/pg-goals.utilities';
import { API_MAX_LIMIT, ApiCommonRoutes, ApiEndpoint, GroupByValues } from '../models/api.models';
import {
  Account,
  AdPlatform,
  CampaignOptimizationValue,
  CompositeId,
  PerformanceGroup,
  UserMaxBid,
  UserMaxBidAPIResponse,
} from '../models/common.models';
import { Currency } from '../models/customer.models';
import {
  AllMetrics,
  BUDGET_AMOUNT,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_CLICKS,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_CLICKS,
  currencyMetrics,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_CRR_CLICKS,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROAS_CLICKS,
  PG_GOAL_METRIC_ROI,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  ROAS,
  ROAS_CLICKS,
  VCPMMetricMaps,
} from '../models/metrics.constants';
import { LoggingService } from '../services/error-logging.service';
import {
  convertMicrostoNumbers,
  getCostFieldName,
  HasDelaySuffix,
  isMonetaryValue,
} from '../services/metrics-calculations.service';
import { StateService } from '../services/state.service';
import { UtilitiesService } from '../services/utilities.service';
import { AccountsState } from '../store/states/accounts/accounts.state';
import { CustomerState } from '../store/states/customer/customer.state';
import { getPlatformLogo } from '../utilities/account.utilities';
import { camelToSnake } from '../utilities/string.utilities';
import { ApiService } from './api.service';

const state = { NO_DATA: '0', ERROR: '-1' };
export function hasHistoricPerfData(summary = {}) {
  if (summary == null) return false;
  return Object.values(summary).some((d: any) => ![0, '—', '-', null].includes(d));
}

@Injectable({
  providedIn: 'root',
})
export class PGAPIService {
  readonly warningTranslations = this.translate.instant('pg-goals.warnings');
  readonly customerId: number = this.store.selectSnapshot(CustomerState.customerId);
  readonly rates = this.store.selectSnapshot(CustomerState.CurrencyRates);
  readonly userCurrency = this.store.selectSnapshot(CustomerState.userCurrency);
  constructor(
    private translate: TranslateService,
    private utilitiesService: UtilitiesService,
    private http: HttpClient,
    private logger: LoggingService,
    private apiService: ApiService,
    private stateService: StateService,
    private store: Store,
  ) {}
  get state() {
    return this.stateService.getState();
  }

  get isDateMoreThan45Days() {
    const dates = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    return differenceInDays(new Date(dates[1]), new Date(dates[0])) > 45;
  }
  hasPG(name, pgId = null): Observable<boolean> {
    return this.http
      .get(
        `${environment.app.resources.dashboard}customers/${
          this.customerId
        }/performance-groups?search_name=${encodeURIComponent(name)}&limit=1000&include=name,performance_group_id`,
        {},
      )
      .pipe(
        map((response: any) => {
          const matchingPGs = response.data.filter(
            (d) => d.performance_group_id != pgId && d.name.toLowerCase() === name.toLowerCase(),
          );

          return matchingPGs.length > 0;
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch pg name', error, 'error');
          return throwError('Failed to fetch pg name');
        }),
      );
  }
  maxBidSuggestion(filterAdspertCampaignId: CompositeId[] = [], currency: Currency): Observable<UserMaxBid> {
    if (!filterAdspertCampaignId?.length)
      return of({ value: MAX_BID_DEFAULT_SUGGESTION_INT * this.rates['EUR-' + currency], currency });

    return this.apiService
      .post({
        endpoint: ApiEndpoint.Customers,
        route: [this.customerId, ApiCommonRoutes.PerformanceGroupsSuggestions],
        data: { filterAdspertCampaignId, filterCurrency: currency },
      })
      .pipe(map(({ data }: any) => MaxBidValueForUI(data?.userMaxBid, null, true)));
  }
  getCurrentGoalMetrics(
    currency,
    optiType: CampaignOptimizationValue,
    campaigns = [],
    performanceGroupIds = null,
    dates = null,
  ) {
    const payload: Record<string, any> = { ...GetDatesRequestInfo(dates), currency };

    if (campaigns.length) payload['filter_adspert_campaign_id'] = campaigns;
    else if (!!performanceGroupIds) payload['filter_performance_group_id'] = performanceGroupIds;

    return this.http
      .post(`${environment.app.resources.dashboard}customers/${this.customerId}/performance`, payload, {})
      .pipe(
        map((res: any) => {
          return {
            data: convertMicrostoNumbers(res.data).map((b) => ({
              [PG_GOAL_METRIC_COST_PER_DAY]: b[getCostFieldName(optiType)],
              [PG_GOAL_METRIC_COST_PER_CLICK]: b[COST_PER_CLICK],
              [PG_GOAL_METRIC_COST_PER_CONVERSION]: b[COST_PER_CONVERSION],
              [PG_GOAL_METRIC_CRR]: b[COST_REVENUE_RATIO],
              [PG_GOAL_METRIC_ROI]: b[RETURN_ON_INVESTMENT],
              [PG_GOAL_METRIC_ROAS]: b[ROAS],
              [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: b[PROFIT_NET],
              [PG_GOAL_METRIC_ROAS_CLICKS]: b[ROAS_CLICKS],
              [PG_GOAL_METRIC_CRR_CLICKS]: b[COST_REVENUE_RATIO_CLICKS],
              [PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS]: b[COST_PER_CONVERSION_CLICKS],

              date: b.date,
            })),
            summary: GoalsSummaryData(res.data, PROFIT_NET, payload.limit),
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch pg metrics', error, 'error');
          return throwError('Failed to fetch pg metrics');
        }),
      );
  }

  getSnapshot(performanceGroupId, dates, currency): Observable<HistoryData[]> {
    function dto(data) {
      return convertMicrostoNumbers(data).map((item: any) => {
        const tmp = { ...item };
        Object.keys(tmp).forEach((d: AllMetrics) => {
          if (HasDelaySuffix(d)) delete tmp[d];
        });
        tmp.goals = !item.goals
          ? []
          : item.goals.map((goal) => ({
              goalValue: isMonetaryValue(goal.goal_type) ? microsToNumber(goal.goal_value) : goal.goal_value,
              goalCurrency: goal.goal_currency,
              goalType: goal.goal_type,
            }));

        return tmp;
      });
    }
    return this.http
      .get(
        `${environment.app.resources.dashboard}customers/${this.customerId}/performance-groups/${performanceGroupId}/snapshot-performance?currency=${currency}&filter_date_ge=${dates[0]}&filter_date_le=${dates[1]}&limit=45`,
        {},
      )
      .pipe(
        map(({ data }: any) => dto(data)),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch snapshot', error, 'error');
          return throwError('Failed to fetch snapshot');
        }),
      );
  }

  createPG(payload): Observable<boolean> {
    return this.http
      .post(`${environment.app.resources.dashboard}customers/${this.customerId}/performance-groups`, payload, {})
      .pipe(
        map(() => true),
        catchError((error: any) => {
          this.logger.logError('Failed to create pg ', error, 'error');
          return throwError(error?.original?.error);
        }),
      );
  }
  updatePG(payload): Observable<boolean> {
    return this.http
      .patch(`${environment.app.resources.dashboard}customers/${this.customerId}/performance-groups`, [payload], {})
      .pipe(
        map(() => true),
        catchError((error: any) => {
          this.logger.logError('Failed to update pg ', error, 'error');
          return throwError(error?.original?.error);
        }),
      );
  }

  savePG(performanceGroupId, payload) {
    return this[!performanceGroupId ? 'createPG' : 'updatePG'](payload);
  }

  getPGInfo(performanceGroupId) {
    const [dateGe, dateLe] = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    return this.apiService
      .getObject<PGListItem>({
        endpoint: ApiEndpoint.Customers,
        route: [this.customerId, ApiCommonRoutes.PerformanceGroups],
        filterValues: { performanceGroupId } as any,
      })
      .pipe(
        switchMap((pg) => {
          return this.apiService
            .getList<{
              performanceGroupId: string;
              metrics: { key: string; value: number | string }[];
            }>({
              endpoint: ApiEndpoint.Customers,
              route: [this.customerId, ApiCommonRoutes.Performance],
              groupBy: GroupByValues.PG,
              params: { currency: this.currencyToUse(pg.data[0]) },
              filterValues: <any>{ dateGe, dateLe, performanceGroupId: [performanceGroupId] },
            })
            .pipe(
              map((perf) => ({
                pg: pg.data[0],
                perf: perf.data[0],
              })),
            );
        }),
      );
  }

  maxBidValue(userMaxBid: UserMaxBidAPIResponse, performanceGroupId: number, currency: Currency): UserMaxBid {
    if (!userMaxBid) return null;

    const val = MaxBidValueForUI(userMaxBid, null, true);
    if (val?.value != null) this.store.dispatch(new SetMaxBid({ [performanceGroupId]: { ...val, currency } }));

    return { ...val, currency };
  }
  getDays() {
    const [start, end] = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    return differenceInDays(new Date(end), new Date(start)) + 1;
  }
  private _getCurrentReachedGoal(item: PGListItem) {
    const currency = this.currencyToUse(item);
    return this.getCurrentGoalMetrics(
      currency,
      item.optimizationType,
      [],
      [item.performanceGroupId],
      this.state[params.GLOBAL_FILTER_DATE_RANGE].split(','),
    ).pipe(
      take(1),
      map((response) => {
        const values = getGoalsCurrentData(item.goals, response.summary, currency);
        item.currentData = values.currentData;
        item.vcpmCurrentData = values.vcpmCurrentData;
        return values.currentData;
      }),
      share(),
    );
  }

  private _getChartData(item: PGListItem) {
    const currency = this.currencyToUse(item);
    const [dateGe, dateLe] = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    return this.apiService
      .getList<PerformanceGroup>({
        endpoint: ApiEndpoint.Customers,
        route: [this.customerId, ApiCommonRoutes.Performance],
        limit: API_MAX_LIMIT,
        groupBy: GroupByValues.DATE,
        sortValues: { date: 1 },
        params: { currency },
        exclude: [
          'account_id',
          'account_is_demo',
          'account_name',
          'account_optimize',
          'account_platform',
          'account_tags_user',
          'adspert_account_id',
          'adspert_campaign_id',
          'adspert_portfolio_id',
          'amz_portfolio_id',
          'amz_portfolio_name',
          'campaign_id',
          'campaign_labels',
          'campaign_name',
          'campaign_optimization_support',
          'campaign_optimization_type',
          'campaign_optimize',
          'campaign_status',
          'campaign_type',
          'performance_group_enabled',
          'performance_group_goal_types',
          'performance_group_goals',
          'performance_group_id',
          'performance_group_name',
          'performance_group_optimization_type',
        ] as any,
        filterValues: <any>{ performanceGroupId: item.performanceGroupId, dateGe, dateLe },
      })
      .pipe(
        take(1),
        map((response) => {
          item.perfData = response.data;
          item.currentChartData = this._getCurrentChartData(item);
          return item.perfData;
        }),
      );
  }

  private _getCurrentChartData(item: PGListItem) {
    return {
      currency: this.currencyToUse(item),
      data: item.perfData.map((d) => {
        const t = {};
        Object.keys(d).forEach(
          (key) =>
            (t[camelToSnake(key)] = isMonetaryValue(<AllMetrics>camelToSnake(key)) ? microsToNumber(d[key]) : d[key]),
        );
        return t;
      }),
    };
  }

  private _getHistoricData(item: PGListItem): Observable<HistoricChartConfig> {
    const currency = this.currencyToUse(item);
    const dates = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    if (this.isDateMoreThan45Days)
      dates[0] = this.utilitiesService.formatDate(addDays(new Date(dates[1]), -45), 'yyyy-MM-dd');

    return this.getSnapshot(item.performanceGroupId, dates, currency).pipe(
      map((res) => {
        if (res.filter((d) => !d.goals?.length).length === res.length) item.historicChart = state.NO_DATA;
        else {
          item.historicChart = { currency, data: res };
        }
        return item.historicChart as HistoricChartConfig;
      }),
      catchError((err) => {
        item.historicChart = state.ERROR;
        return of(undefined);
      }),
    );
  }

  getDefaultAccountsInfo(defaultAccounts: number[]): Account[] {
    if (defaultAccounts?.length) {
      return defaultAccounts
        .map((d) => this.store.selectSnapshot(AccountsState.getSelectedAccount(d)))
        .filter((d) => !!d)
        .map((d) => ({ ...d, platform: getPlatformLogo(d.platform) as AdPlatform }));
    }
    return [];
  }
  getCampaigns(item: PGListItem, value) {
    const currency = this.currencyToUse(item);
    const [dateGe, dateLe] = this.state[params.GLOBAL_FILTER_DATE_RANGE].split(',');
    const searchQuery = value
      ? {
          searchFields:
            'account_name,adspert_account_id,account_id,account_tags_user,campaign_name,campaign_id,campaign_labels',
          searchTerm: value,
        }
      : {};
    return this.apiService
      .getList<any>({
        endpoint: ApiEndpoint.Customers,
        route: [this.customerId, ApiCommonRoutes.Performance],
        limit: API_MAX_LIMIT,
        ...searchQuery,
        groupBy: GroupByValues.CAMPAIGN,
        sortValues: { campaignStatus: 1 },
        filterValues: {
          performanceGroupId: item.performanceGroupId,
          dateGe,
          dateLe,
        } as any,
        params: { currency },
      })
      .pipe(
        take(1),
        map(({ data }) => GetCampaignsWithAllMetrics(data) as SelectCampaignsListItem[]),
      );
  }
  currencyToUse(item: PGListItem) {
    return item.usePGCurrency ? item.pgCurrency : this.userCurrency;
  }
  transformPG(data: PGListItem): PGListItem {
    const warnings = FormattedWarnings(ExlcudeFewWarningsForEmptyPG(data), this.warningTranslations);
    const d: any = { ...data, ...warnings };

    d.processing = false;
    d.originalName = d.name;
    if (typeof data.usePGCurrency !== 'boolean') d.usePGCurrency = true;
    else d.usePGCurrency = data.usePGCurrency;
    d.currencyChangeLoader = true;
    d.pgCurrency = d.pgCurrency;
    d.goals = d.goals.map(adjustMonetaryValues).map((e) => ({ ...e, goalCurrency: d.pgCurrency }));
    d.goalsWithUserCurrency = getGoalsWithUserCurrency(d, this.userCurrency, this.rates);
    d.userMaxBid = this.maxBidValue(d.userMaxBid, d.performanceGroupId, d.pgCurrency);
    d.userMaxBidWithUserCurrency = getMaxBidWithUserCurrency(d, this.userCurrency, this.rates);
    d.current$ = this._getCurrentReachedGoal(d);
    d.perf$ = this._getChartData(d);
    d.suggestedBid$ = this.maxBidSuggestion(d.campaigns, d.pgCurrency);
    d.historic$ = this._getHistoricData(d);
    d.defaultForAccountsDetails = this.getDefaultAccountsInfo(d?.defaultForAccounts);

    d.metrics = d.metrics.map((item) => {
      let { key, value } = item;
      if (currencyMetrics.includes(key) && d.usePGCurrency)
        value = value * this.rates[this.userCurrency + '-' + d.pgCurrency];
      return { key, value };
    });
    d.budgetAmount = d.metrics.find((e) => e.key === BUDGET_AMOUNT)?.value || null;

    d.vcpmMetrics = [];
    if (!d.goals.length) {
      d.defaultMetrics = [];
    } else {
      d.defaultMetrics = d.metrics.filter(({ key }) => mapMetricsForOpAndGoal(d)?.includes(key));
      if (d.optimizationType === OptimizationTypes.VCPM) {
        d.vcpmMetrics = d.defaultMetrics.map((e) => {
          const v = VCPMMetricMaps[e.key];
          if (v) return d.metrics.find(({ key }) => v === key);

          return null;
        });
      }
    }

    return <PGListItem>d;
  }
}
